import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { QawaemErpModalFormTabBar } from '../../products/saudi/components/QawaemErpModalFormTabs';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { CustomDatePicker, CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import {
  CheckSquare, 
  Plus, 
  Search, 
  AlertCircle, 
  CheckCircle, 
  Clock,
  Calendar,
  ChevronDown,
  ChevronUp,
  Filter,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  Grid,
  RefreshCcw,
  X,
  LayoutList,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Trash2,
  DollarSign,
  LayoutGrid,
  Shield,
  Layers,
  TrendingUp
} from 'lucide-react';
import '../../shared/styles/GPSTrackingDatePicker.css';
import localApiAdapter from '../../core/services/localApiAdapter';
import toast from 'react-hot-toast';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import {
  mapReconciliationFromApi,
  mapReconciliationToApi,
  resolveCaisseId,
} from './treasuryApiMappers';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { SAUDI_REGISTER_LABEL_KEYS, SAUDI_DEFAULT_REGISTERS } from './saudiTreasuryDefaults';
import {
  reconciliationExportHeaders,
  mapReconciliationsForExport,
  exportTreasuryCSV,
  exportTreasuryExcel,
  exportTreasuryPDF,
  reconciliationImportHeaders,
  triggerTreasuryImport,
  parseTreasuryImport,
} from './treasuryExportHelpers';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  QAWAEM_MODAL_OVERLAY_CLASS,
  getQawaemModalPanelClass,
  getQawaemModalPanelStyle,
} from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { buildCaisseColumnDefs, useCaisseColumnVisibility, caisseTableColSpan, buildCaisseModalFormTabs, nextCaisseEntityRef, sliceCaisseListItems, findCaisseAccountByName, resolveCaisseAccountBalance, CAISSE_FORM_GRID, useCaisseAccounts } from './caisseListPageUtils';
import QawaemPageTitleWithFilters from '../../products/saudi/components/QawaemPageTitleWithFilters';
import QawaemImportExportToolbar from '../../products/saudi/components/QawaemImportExportToolbar';
import { QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useTreasuryConfig, useTreasuryListViewMode } from './useTreasuryConfig';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';

const CaisseReconciliationPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.caisse.pages.caisseReconciliationPage.${key}`, opts);
  const exportP = (key, opts) => t(`modules.caisse.pages.treasuryExportHelpers.${key}`, opts);
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;
  const { settings } = useSettings();
  const treasuryConfig = useTreasuryConfig();
  const [viewMode, setViewMode] = useTreasuryListViewMode(treasuryConfig);
  const { accounts: caisseAccounts } = useCaisseAccounts();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'المطابقة' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'المطابقات' });

  const REGISTER_LABELS = SAUDI_REGISTER_LABEL_KEYS;
  const translateRegister = (val) => {
    const labelKey = REGISTER_LABELS[val];
    return labelKey ? p(labelKey) : val;
  };
  const dateLocale = getBrandLocale(i18n.language);
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(dateLocale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };
  const formatAmount = (amount) => formatBrandMoney(amount);

  const statusOptions = [
    { value: 'pending', label: p('status_pending'), isDefault: true },
    { value: 'validated', label: p('status_validated'), isDefault: true },
  ];

  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReconciliation, setSelectedReconciliation] = useState(null);
  
  const [reconciliationFormTab, setReconciliationFormTab] = useState('general');

  const reconciliationFormTabDefs = useMemo(() => ([
    { id: 'general', label: p('section_general_info'), icon: Calendar, iconClassName: 'text-emerald-400' },
    { id: 'notes', label: p('section_notes_status'), icon: FileText, iconClassName: 'text-yellow-400' },
  ]), [p]);
  const RECONCILIATION_FIELD_COUNT = 8;
  const reconciliationFormTabs = useMemo(
    () => buildCaisseModalFormTabs(reconciliationFormTabDefs, RECONCILIATION_FIELD_COUNT),
    [reconciliationFormTabDefs],
  );
  
  const [currentView, setCurrentView] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  const { filters: columnFilters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [reconciliations, setReconciliations] = useState([]);
  const [defaultCaisseId, setDefaultCaisseId] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const principal = caisseAccounts.find((c) => c.type === 'principal');
    if (principal?.id) setDefaultCaisseId(principal.id);
  }, [caisseAccounts]);

  const buildNewReconciliationForm = useCallback(() => ({
    date: new Date().toISOString().split('T')[0],
    reference: nextCaisseEntityRef(
      settings,
      'caisseReconciliation',
      treasuryConfig.reconciliationPrefix || 'RAP',
      reconciliations,
      (r) => r.reference,
    ),
    periode: '',
    caisse: '',
    soldeSysteme: 0,
    soldePhysique: 0,
    ecart: 0,
    notes: '',
    status: '',
  }), [settings, treasuryConfig.reconciliationPrefix, reconciliations]);

  const [formData, setFormData] = useState(() => ({
    date: new Date().toISOString().split('T')[0],
    reference: '',
    periode: '',
    caisse: '',
    soldeSysteme: 0,
    soldePhysique: 0,
    ecart: 0,
    notes: '',
    status: '',
  }));
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const registerOptions = useMemo(() => {
    if (caisseAccounts.length) {
      return caisseAccounts.map((a) => ({
        value: a.name,
        label: translateRegister(a.name) || a.name,
        isDefault: true,
      }));
    }
    return SAUDI_DEFAULT_REGISTERS.map((r) => ({
      value: r.value,
      label: p(r.labelKey),
      isDefault: true,
    }));
  }, [caisseAccounts, p]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadReconciliations();
  }, []);

  const loadReconciliations = async () => {
    setLoading(true);
    try {
      const rows = await localApiAdapter.get('/treasury/reconciliation');
      setReconciliations(sortRecordsNewestFirst((Array.isArray(rows) ? rows : []).map(mapReconciliationFromApi)));
    } catch (error) {
      console.error('Failed to load reconciliations:', error);
      toast.error(p('load_error') || t('common.load_error'));
      setReconciliations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const caisseId = resolveCaisseId(formData.caisse, caisseAccounts) || defaultCaisseId;
    const reference = formData.reference || nextCaisseEntityRef(
      settings,
      'caisseReconciliation',
      treasuryConfig.reconciliationPrefix || 'RAP',
      reconciliations,
      (r) => r.reference,
    );
    const payload = mapReconciliationToApi({ ...formData, reference, caisse_id: caisseId }, caisseId);

    try {
      if (selectedReconciliation?.id) {
        await localApiAdapter.put(`/treasury/reconciliation/${selectedReconciliation.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/treasury/reconciliation', payload);
        notifyCreated(showNotify, entityLabel);
      }
      await loadReconciliations();
      resetForm();
    } catch (error) {
      console.error('Reconciliation save error:', error);
      notifyError(showNotify, error.message || p('load_error'));
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedRecords.length) return;
    if (!window.confirm(p('delete_confirm', { count: selectedRecords.length }))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`/treasury/reconciliation/${id}`)));
      clearSelection();
      await loadReconciliations();
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
    } catch (error) {
      console.error('Delete reconciliations error:', error);
      notifyError(showNotify, error.message || p('load_error'));
    }
  };

  const handleEdit = (reconciliation) => {
    setSelectedReconciliation(reconciliation);
    setFormData(reconciliation);
    setReconciliationFormTab('general');
    setShowModal(true);
  };

  const handleView = (reconciliation) => {
    setSelectedReconciliation(reconciliation);
    setShowViewModal(true);
  };

  const handleRegisterChange = (registerName) => {
    const account = findCaisseAccountByName(caisseAccounts, registerName);
    const systemBalance = resolveCaisseAccountBalance(account);
    setFormData((prev) => ({
      ...prev,
      caisse: registerName,
      soldeSysteme: account ? systemBalance : prev.soldeSysteme,
      ecart: account ? Number(prev.soldePhysique || 0) - systemBalance : prev.ecart,
    }));
  };

  const applyRegisterBalance = useCallback((registerName, accounts = caisseAccounts) => {
    const account = findCaisseAccountByName(accounts, registerName);
    if (!account) return null;
    const systemBalance = resolveCaisseAccountBalance(account);
    return {
      caisse: registerName,
      soldeSysteme: systemBalance,
      ecart: 0,
    };
  }, [caisseAccounts]);

  const resetForm = () => {
    setFormData(buildNewReconciliationForm());
    setSelectedReconciliation(null);
    setReconciliationFormTab('general');
    setShowModal(false);
  };

  const openModal = () => {
    setSelectedReconciliation(null);
    const baseForm = buildNewReconciliationForm();
    const defaultRegister = registerOptions[0]?.value || '';
    const balancePatch = defaultRegister ? applyRegisterBalance(defaultRegister) : null;
    setFormData(balancePatch ? { ...baseForm, ...balancePatch } : baseForm);
    setReconciliationFormTab('general');
    setShowModal(true);
  };

  const stats = {
    total: reconciliations.length,
    validated: reconciliations.filter(r => r.status === 'validated').length,
    pending: reconciliations.filter(r => r.status === 'pending').length,
    withGaps: reconciliations.filter(r => r.ecart !== 0).length
  };

  const columnFilterRules = useMemo(() => [
    textColumnRule('code', (r) => r.reference || ''),
    textColumnRule('date', (r) => formatDate(r.date)),
    textColumnRule('period', (r) => r.periode || ''),
    textColumnRule('register', (r) => translateRegister(r.caisse)),
    textColumnRule('systemBalance', (r) => formatAmount(r.soldeSysteme)),
    textColumnRule('physicalBalance', (r) => formatAmount(r.soldePhysique)),
    textColumnRule('gap', (r) => formatAmount(r.ecart)),
    exactColumnRule('status', (r) => r.status || ''),
  ], [formatDate, formatAmount, translateRegister]);

  const filteredReconciliations = useMemo(() => {
    const base = reconciliations.filter((reconciliation) => {
      const matchesView = currentView === 'all' || reconciliation.status === currentView;
      const matchesSearch = !searchTerm ||
        reconciliation.caisse?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reconciliation.periode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reconciliation.reference?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesView && matchesSearch;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [reconciliations, currentView, searchTerm, columnFilters, columnFilterRules]);

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useQawaemFilteredRowSelection(filteredReconciliations);

  const clickableStatusOptions = useMemo(() => ([
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    { value: 'validated', label: p('status_validated'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
  ]), [p]);

  const reconciliationColumnKeys = useMemo(
    () => ['code', 'date', 'period', 'register', 'systemBalance', 'physicalBalance', 'gap', 'status', 'actions'],
    [],
  );
  const pagedReconciliations = useMemo(
    () => sliceCaisseListItems(filteredReconciliations, treasuryConfig.itemsPerPage),
    [filteredReconciliations, treasuryConfig.itemsPerPage],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useCaisseColumnVisibility(reconciliationColumnKeys);
  const qawaemColumns = useMemo(
    () => buildCaisseColumnDefs([
      { key: 'code', labelKey: 'col_code' },
      { key: 'date', labelKey: 'col_date' },
      { key: 'period', labelKey: 'col_period' },
      { key: 'register', labelKey: 'col_register' },
      { key: 'systemBalance', labelKey: 'col_system_balance' },
      { key: 'physicalBalance', labelKey: 'col_physical_balance' },
      { key: 'gap', labelKey: 'col_gap' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, columnVisibility),
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = caisseTableColSpan(qawaemColumns);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/treasury/reconciliation/${recordId}`, { status: newStatus });
      notifyUpdated(showNotify, entityLabel);
      await loadReconciliations();
    } catch (error) {
      console.error('Reconciliation status change error:', error);
      notifyError(showNotify, error.message || p('load_error'));
    }
  };

  const getStatusBadge = (status) => {
    const config = clickableStatusOptions.find((s) => s.value === status) || clickableStatusOptions[0];
    const { label, icon: Icon, style } = config;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border inline-flex items-center gap-1.5 ${style}`}>
        {Icon ? <Icon size={14} /> : null}
        {label}
      </span>
    );
  };

  const viewFilterLabel = (view) => {
    if (view === 'all') return p('filter_all');
    if (view === 'pending') return p('filter_pending');
    return p('filter_validated');
  };
  const exportRows = mapReconciliationsForExport(filteredReconciliations);
  const exportHeaders = reconciliationExportHeaders(exportP);
  const exportBaseName = `treasury_reconciliation_${new Date().toISOString().slice(0, 10)}`;

  const handleExportCSV = () => {
    if (!exportRows.length) { toast.error(p('empty_message') || p('load_error')); return; }
    exportTreasuryCSV(exportRows, exportHeaders, `${exportBaseName}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    if (!exportRows.length) { toast.error(p('empty_message') || p('load_error')); return; }
    await exportTreasuryExcel(exportRows, exportHeaders, `${exportBaseName}.xlsx`, p('title'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    if (!exportRows.length) { toast.error(p('empty_message') || p('load_error')); return; }
    setIsGeneratingPDF(true);
    try {
      await exportTreasuryPDF(p('title'), exportHeaders, exportRows);
    } finally {
      setIsGeneratingPDF(false);
      setShowExportDropdown(false);
    }
  };

  const handleImportFile = (file) => {
    const headers = reconciliationImportHeaders(exportP);
    const schema = {
      requiredFields: ['date', 'periode'],
      validators: {
        soldeSysteme: (v) => !v || !Number.isNaN(Number(v)),
        soldePhysique: (v) => !v || !Number.isNaN(Number(v)),
      },
    };
    parseTreasuryImport(file, headers, schema)
      .then(async (result) => {
        const rows = result?.data || result?.rows || [];
        if (!rows.length) {
          toast.error(p('empty_message') || t('common.error'));
          return;
        }
        let imported = 0;
        for (const row of rows) {
          const payload = mapReconciliationToApi({
            date: row.date,
            periode: row.periode,
            soldeSysteme: Number(row.soldeSysteme) || 0,
            soldePhysique: Number(row.soldePhysique) || 0,
            caisse_id: defaultCaisseId,
            status: 'pending',
          }, defaultCaisseId);
          await localApiAdapter.post('/treasury/reconciliation', payload);
          imported += 1;
        }
        toast.success(p('import_success', { count: imported }));
        await loadReconciliations();
      })
      .catch((err) => {
        console.error('Import reconciliations error:', err);
        toast.error(err.message || p('load_error'));
      })
      .finally(() => setShowImportDropdown(false));
  };

  return (
    <div className="space-y-6 clients-container">
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div className="flex-1 min-w-0">
          <QawaemPageTitleWithFilters
            filters={selectedRecords.length > 0 ? (
              <button
                type="button"
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
              >
                <Trash2 size={16} />
                <span>{t('common.delete')} ({selectedRecords.length})</span>
              </button>
            ) : null}
          >
            <h1 className="text-3xl font-bold text-white">
              {p('title')}
              <button onClick={loadReconciliations} className="refresh-button ml-4" title={t('common.refresh')}>
                <RefreshCcw size={20} />
              </button>
            </h1>
          </QawaemPageTitleWithFilters>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3 items-center">
          <QawaemImportExportToolbar
            importOpen={showImportDropdown}
            exportOpen={showExportDropdown}
            onToggleImport={() => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); }}
            onToggleExport={() => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); }}
            importRef={importDropdownRef}
            exportRef={exportDropdownRef}
            importLabel={p('import', { defaultValue: 'استيراد' })}
            exportLabel={p('export', { defaultValue: 'تصدير' })}
            generatingLabel={t('common.loading')}
            isGeneratingPDF={isGeneratingPDF}
            onImportCSV={() => { triggerTreasuryImport(handleImportFile, '.csv'); setShowImportDropdown(false); }}
            onImportExcel={() => { triggerTreasuryImport(handleImportFile); setShowImportDropdown(false); }}
            onExportCSV={handleExportCSV}
            onExportExcel={handleExportExcel}
            onExportPDF={handleExportPDF}
          />
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions 
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-400' 
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
            title={p('advanced_options')}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          
          <button
            onClick={openModal}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_reconciliation')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards - GPS Glow Effect */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: p('stat_total'), value: stats.total, icon: CheckSquare, color: 'from-blue-500 to-blue-600' },
          { label: p('stat_validated'), value: stats.validated, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { label: p('stat_pending'), value: stats.pending, icon: Clock, color: 'from-amber-500 to-amber-600' },
          { label: p('stat_with_gaps'), value: stats.withGaps, icon: AlertCircle, color: 'from-red-500 to-red-600' },
          { label: p('stat_validation_rate'), value: stats.total > 0 ? `${Math.round((stats.validated / stats.total) * 100)}%` : '0%', icon: TrendingUp, color: 'from-purple-500 to-purple-600' }
        ].slice(0, 4).map((stat, index) => (
          <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.label}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                  {['all', 'pending', 'validated'].map(view => (
                    <button key={view} onClick={() => setCurrentView(view)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${currentView === view ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                      <span>{viewFilterLabel(view)}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Data Table with Integrated Header */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' ? (
          <div className="overflow-x-auto table-horizontal-scroll">
            <table className="w-full clients-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                    selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                    deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء التحديد' })}
                  />
                  {isColumnVisible('code') ? (
                    <TableColumnHeaderFilter columnKey="code" label={p('col_code')} value={columnFilters.code || ''} onChange={(v) => setFilter('code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('date') ? (
                    <TableColumnHeaderFilter columnKey="date" label={p('col_date')} value={columnFilters.date || ''} onChange={(v) => setFilter('date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('period') ? (
                    <TableColumnHeaderFilter columnKey="period" label={p('col_period')} value={columnFilters.period || ''} onChange={(v) => setFilter('period', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('register') ? (
                    <TableColumnHeaderFilter columnKey="register" label={p('col_register')} value={columnFilters.register || ''} onChange={(v) => setFilter('register', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('systemBalance') ? (
                    <TableColumnHeaderFilter columnKey="systemBalance" label={p('col_system_balance')} value={columnFilters.systemBalance || ''} onChange={(v) => setFilter('systemBalance', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('physicalBalance') ? (
                    <TableColumnHeaderFilter columnKey="physicalBalance" label={p('col_physical_balance')} value={columnFilters.physicalBalance || ''} onChange={(v) => setFilter('physicalBalance', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('gap') ? (
                    <TableColumnHeaderFilter columnKey="gap" label={p('col_gap')} value={columnFilters.gap || ''} onChange={(v) => setFilter('gap', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  {isColumnVisible('status') ? (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusOptions.map((o) => ({ value: o.value, label: o.label }))} value={columnFilters.status || ''} onChange={(v) => setFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="bg-white/5">
                {pagedReconciliations.map((reconciliation) => (
                  <tr key={reconciliation.id} className="hover:bg-white/10 transition-colors border-b border-white/10">
                    <QawaemTableSelectRowCell
                      checked={selectedRecords.includes(reconciliation.id)}
                      onChange={() => handleSelectRecord(reconciliation.id)}
                    />
                    {isColumnVisible('code') ? (
                      <td className="px-6 py-4 text-white font-mono text-sm">{reconciliation.reference}</td>
                    ) : null}
                    {isColumnVisible('date') ? (
                      <td className="px-6 py-4 text-white/80">{formatDate(reconciliation.date)}</td>
                    ) : null}
                    {isColumnVisible('period') ? (
                      <td className="px-6 py-4 text-white font-semibold">{reconciliation.periode}</td>
                    ) : null}
                    {isColumnVisible('register') ? (
                      <td className="px-6 py-4 text-white/80">{translateRegister(reconciliation.caisse)}</td>
                    ) : null}
                    {isColumnVisible('systemBalance') ? (
                      <td className="px-6 py-4 text-white font-bold">{formatAmount(reconciliation.soldeSysteme)}</td>
                    ) : null}
                    {isColumnVisible('physicalBalance') ? (
                      <td className="px-6 py-4 text-white font-bold">{formatAmount(reconciliation.soldePhysique)}</td>
                    ) : null}
                    {isColumnVisible('gap') ? (
                      <td className="px-6 py-4">
                        <span className={`font-bold ${reconciliation.ecart === 0 ? 'text-green-400' : reconciliation.ecart > 0 ? 'text-blue-400' : 'text-red-400'}`}>
                          {reconciliation.ecart > 0 ? '+' : ''}{formatAmount(reconciliation.ecart)}
                        </span>
                      </td>
                    ) : null}
                    {isColumnVisible('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={reconciliation.status}
                            recordId={reconciliation.id}
                            options={clickableStatusOptions}
                            onChange={handleStatusChange}
                            title={t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                        onView={() => handleView(reconciliation)}
                        onEdit={() => handleEdit(reconciliation)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {pagedReconciliations.map((reconciliation, index) => (
              <motion.div key={reconciliation.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-4">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <CheckSquare size={20} className="text-emerald-400" />
                        {reconciliation.periode}
                      </h3>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('section_general_info')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div><span className="font-medium">{p('label_date')}:</span> {formatDate(reconciliation.date)}</div>
                          <div><span className="font-medium">{p('label_period')}:</span> {reconciliation.periode}</div>
                          <div><span className="font-medium">{p('label_register')}:</span> {translateRegister(reconciliation.caisse)}</div>
                        </div>
                      </div>
                      
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('section_system_balance')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div className="p-2 bg-blue-500/10 rounded border border-blue-500/30">
                            <span className="font-medium text-blue-300 text-xs">{p('label_system_amount')}:</span>
                            <p className="text-blue-200 text-sm mt-1 font-semibold">{formatAmount(reconciliation.soldeSysteme)}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('section_physical_balance')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div className="p-2 bg-green-500/10 rounded border border-green-500/30">
                            <span className="font-medium text-green-300 text-xs">{p('label_physical_amount')}:</span>
                            <p className="text-green-200 text-sm mt-1 font-semibold">{formatAmount(reconciliation.soldePhysique)}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('section_gap')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div className={`p-2 rounded border ${reconciliation.ecart === 0 ? 'bg-green-500/10 border-green-500/30' : reconciliation.ecart > 0 ? 'bg-blue-500/10 border-blue-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                            <span className={`font-medium text-xs ${reconciliation.ecart === 0 ? 'text-green-300' : reconciliation.ecart > 0 ? 'text-blue-300' : 'text-red-300'}`}>{p('label_gap_amount')}:</span>
                            <p className={`text-sm mt-1 font-bold ${reconciliation.ecart === 0 ? 'text-green-200' : reconciliation.ecart > 0 ? 'text-blue-200' : 'text-red-200'}`}>{reconciliation.ecart > 0 ? '+' : ''}{formatAmount(reconciliation.ecart)}</p>
                          </div>
                          {reconciliation.notes && (
                            <div className="mt-2 p-2 bg-white/5 rounded">
                              <span className="font-medium text-white/80 text-xs">{p('label_notes')}:</span>
                              <p className="text-white/60 text-xs mt-1">{reconciliation.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-2 ml-4">
                    <QawaemClickableStatusBadge
                      status={reconciliation.status}
                      recordId={reconciliation.id}
                      options={clickableStatusOptions}
                      onChange={handleStatusChange}
                    />
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(reconciliation)}
                      onEdit={() => handleEdit(reconciliation)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className={QAWAEM_MODAL_OVERLAY_CLASS} onClick={resetForm} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={getQawaemModalPanelClass(isLight)}
            style={getQawaemModalPanelStyle(isLight, '1200px')}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <CheckSquare className="h-6 w-6 text-blue-400" />
                {selectedReconciliation ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button 
                  type="submit" 
                  form="reconciliation-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {selectedReconciliation ? p('btn_update') : p('btn_create')}
                </button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>
            </div>
            
            {reconciliationFormTabs.length > 0 ? (
              <QawaemErpModalFormTabBar
                tabs={reconciliationFormTabs}
                activeTab={reconciliationFormTab}
                onTabChange={setReconciliationFormTab}
                isLight={isLight}
                ariaLabel={selectedReconciliation ? p('modal_edit') : p('modal_new')}
              />
            ) : null}
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="reconciliation-form" onSubmit={handleSubmit}>
                {(reconciliationFormTabs.length === 0 || reconciliationFormTab === 'general') && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_code')} <span className="qawaem-required-mark">*</span></label>
                      <input type="text" required value={formData.reference || ''} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono" placeholder={p('col_code')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_date')} <span className="qawaem-required-mark">*</span></label>
                      <CustomDatePicker value={formData.date} onChange={(dateString) => setFormData({ ...formData, date: dateString })} placeholder={p('select_date')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_period')} <span className="qawaem-required-mark">*</span></label>
                      <input type="text" required value={formData.periode} onChange={(e) => setFormData({ ...formData, periode: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('period_placeholder')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_register')} <span className="qawaem-required-mark">*</span></label>
                      <CustomSelect value={formData.caisse} onChange={handleRegisterChange} options={registerOptions} placeholder={p('select_register')} searchable={true} creatable={false} autoSelect={false} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_system_amount_sar')} <span className="qawaem-required-mark">*</span></label>
                      <CustomNumberInput step={0.01} value={formData.soldeSysteme} onChange={(v) => setFormData({ ...formData, soldeSysteme: v, ecart: Number(formData.soldePhysique || 0) - Number(v || 0) })} placeholder="0.00" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_physical_amount_sar')} <span className="qawaem-required-mark">*</span></label>
                      <CustomNumberInput step={0.01} value={formData.soldePhysique} onChange={(v) => setFormData({ ...formData, soldePhysique: v, ecart: Number(v || 0) - Number(formData.soldeSysteme || 0) })} placeholder="0.00" />
                    </div>
                    {reconciliationFormTabs.length === 0 ? (
                      <>
                        <div className="min-w-0">
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')}</label>
                          <CustomSelect value={formData.status} onChange={(val) => setFormData({ ...formData, status: val })} options={statusOptions} placeholder={p('select_status')} searchable={true} creatable={true} autoSelect={false} />
                        </div>
                        <div className="min-w-0 md:col-span-2">
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                          <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('notes_placeholder')} />
                        </div>
                      </>
                    ) : null}
                  </div>
                )}
                {reconciliationFormTabs.length > 0 && reconciliationFormTab === 'notes' && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')}</label>
                      <CustomSelect value={formData.status} onChange={(val) => setFormData({ ...formData, status: val })} options={statusOptions} placeholder={p('select_status')} searchable={true} creatable={true} autoSelect={false} />
                    </div>
                    <div className="min-w-0 md:col-span-2">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                      <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('notes_placeholder')} />
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {showViewModal && selectedReconciliation ? (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => { setShowViewModal(false); setSelectedReconciliation(null); }}
          title={p('modal_details')}
          icon={CheckSquare}
          maxWidth="1100px"
        >
            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Calendar size={18} className="text-emerald-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_general_info')}</h3>
                </div>
                <div className={CAISSE_FORM_GRID}>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_date')}</p>
                    <p className="text-white font-semibold">{formatDate(selectedReconciliation.date)}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_period')}</p>
                    <p className="text-white font-semibold">{selectedReconciliation.periode}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_register')}</p>
                    <p className="text-white font-semibold">{translateRegister(selectedReconciliation.caisse)}</p>
                  </div>
                </div>
              </div>

              <div className={CAISSE_FORM_GRID}>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <DollarSign size={18} className="text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_system_balance')}</h3>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_amount')}</p>
                    <p className="text-white font-bold text-xl">{formatAmount(selectedReconciliation.soldeSysteme)}</p>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <DollarSign size={18} className="text-green-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_physical_balance')}</h3>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_amount')}</p>
                    <p className="text-white font-bold text-xl">{formatAmount(selectedReconciliation.soldePhysique)}</p>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <TrendingUp size={18} className={selectedReconciliation.ecart === 0 ? 'text-green-400' : selectedReconciliation.ecart > 0 ? 'text-blue-400' : 'text-red-400'} />
                    <h3 className="text-lg font-semibold text-white">{p('section_gap')}</h3>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_difference')}</p>
                    <p className={`font-bold text-xl ${selectedReconciliation.ecart === 0 ? 'text-green-400' : selectedReconciliation.ecart > 0 ? 'text-blue-400' : 'text-red-400'}`}>
                      {selectedReconciliation.ecart > 0 ? '+' : ''}{formatAmount(selectedReconciliation.ecart)}
                    </p>
                  </div>
                </div>
              </div>

              {(selectedReconciliation.notes || selectedReconciliation.status) ? (
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <FileText size={18} className="text-yellow-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_additional_info')}</h3>
                  </div>
                  <div className={CAISSE_FORM_GRID}>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_status')}</p>
                      {getStatusBadge(selectedReconciliation.status)}
                    </div>
                    {selectedReconciliation.notes ? (
                      <div className="md:col-span-2">
                        <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_notes')}</p>
                        <p className="text-white/70 text-sm italic">{selectedReconciliation.notes}</p>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
        </QawaemErpViewModal>
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default CaisseReconciliationPage;


